import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Signal } from "@ct/shared-types";
import { fetchRecentSignals } from "../../lib/api.js";
import { marketSocket } from "../../lib/ws.js";

/**
 * Live ranked signal feed. Bootstraps from REST `/v1/signals/recent`, then
 * appends live signals received over `signals:all`.
 */
export function SignalPanel() {
  const [signals, setSignals] = useState<Signal[]>([]);

  const initial = useQuery({
    queryKey: ["recent-signals"],
    queryFn: () => fetchRecentSignals(50),
    refetchOnMount: true,
  });

  useEffect(() => {
    if (initial.data) setSignals(initial.data);
  }, [initial.data]);

  useEffect(() => {
    return marketSocket.subscribe("signals:all", (_c, payload) => {
      const s = payload as Signal;
      setSignals((prev) => {
        if (prev.some((p) => p.id === s.id)) return prev;
        return [s, ...prev].slice(0, 100);
      });
    });
  }, []);

  return (
    <aside className="flex h-full flex-col bg-zinc-950">
      <header className="flex items-center justify-between border-b border-zinc-800 px-3 py-2 text-xs uppercase tracking-wider text-zinc-400">
        <span>Live signals</span>
        <span className="text-zinc-600">{signals.length}</span>
      </header>
      <div className="flex-1 overflow-y-auto">
        {signals.length === 0 ? (
          <div className="p-4 text-xs text-zinc-500">
            No signals yet. Strategy engine warming up — needs ≥ 220 closed
            candles for the highest-warmup strategy.
          </div>
        ) : (
          <ul>
            {signals.map((s) => (
              <li
                key={s.id}
                className="border-b border-zinc-900 px-3 py-2 text-xs hover:bg-zinc-900/40"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-zinc-200">{s.symbol}</span>
                  <span className="se-pill se-pill-buy">BUY</span>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-x-2 text-zinc-400">
                  <span>strat: {s.strategyId}</span>
                  <span>tf: {s.timeframe}</span>
                  <span>entry: {s.entryPrice.toFixed(4)}</span>
                  <span>SL: {s.stopLoss.toFixed(4)}</span>
                  <span>RRR: {s.riskRewardRatio.toFixed(2)}</span>
                  <span>conf: {(s.confidence * 100).toFixed(0)}%</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
